/**
 * Post Editor Sidebar - Frontend Script
 */

( function() {
	'use strict';

	// Shared helper: run a recipe and apply standard actions
	// SIMPLIFIED: Just use database content, no form saving
	async function runRecipeAndApply({ recipe, postId, apply = [] }) {
		const config = window.xtraPostEditorSidebar || {};
		if ( ! postId ) {
			throw new Error( 'No post ID provided' );
		}

		// 1) Load post data from database
		const postResp = await fetch( `${config.apiUrl}wp/v2/posts/${postId}?context=edit`, {
			method: 'GET',
			headers: { 'X-WP-Nonce': config.nonce }
		} );
		if ( ! postResp.ok ) {
			throw new Error( 'Failed to load post' );
		}
		const post = await postResp.json();
		
		// 2) Run recipe with context from database
		const context = {
			title: (post?.title?.raw || '').toString(),
			content: (post?.content?.raw || '').toString(),
			excerpt: (post?.excerpt?.raw || '').toString()
		};
		
		const runResp = await fetch( `${config.apiUrl}xtra-ai/v1/run-recipe`, {
			method: 'POST',
			headers: {
				'X-WP-Nonce': config.nonce,
				'Content-Type': 'application/json'
			},
			body: JSON.stringify( {
				recipe,
				post_id: postId,
				context
			} )
		} );
		const runData = await runResp.json();
		if ( ! runResp.ok || ! runData?.success ) {
			throw new Error( runData?.error || 'AI request failed' );
		}

		// 3) Extract response object
		const r = runData?.data?.response;
		let respObj = {};
		if ( r ) {
			if ( typeof r === 'object' && ! Array.isArray( r ) && ! r.format ) {
				respObj = r;
			} else if ( r?.format && r?.content ) {
				try { respObj = JSON.parse( r.content ); } catch (e) { respObj = {}; }
			} else if ( typeof r === 'string' ) {
				try { respObj = JSON.parse( r ); } catch (e) { respObj = {}; }
			} else if ( Array.isArray( r ) ) {
				respObj = { list: r };
			}
		}

		// 4) Apply mapped actions - REVISION BEFORE UPDATE
		for ( const step of apply ) {
			let value = respObj?.[ step.from ];
			if ( ! value && Array.isArray( respObj?.list ) && step.from === 'title' ) {
				value = respObj.list[0];
			}
			if ( ! value ) {
				throw new Error( `Missing "${step.from}" in AI response` );
			}

			// REVISION BEFORE UPDATE
			await fetch( `${config.apiUrl}xtra-blocks/v1/revisions/capture`, {
				method: 'POST',
				headers: {
					'X-WP-Nonce': config.nonce,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify( { post_id: postId } )
			} );

			// UPDATE with AI result
			const execResp = await fetch( `${config.apiUrl}xtra-ai/v1/execute-action`, {
				method: 'POST',
				headers: {
					'X-WP-Nonce': config.nonce,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify( {
					action: step.action,
					value: String( value ),
					post_id: postId,
					...( step.meta_key ? { meta_key: step.meta_key } : {} )
				} )
			} );
			const execData = await execResp.json();
			if ( ! execResp.ok || ! execData?.success ) {
				throw new Error( execData?.error || 'Failed to apply action' );
			}
		}

		return true;
	}

	// Action Registry - Define all available actions
	const actionRegistry = {
		sampleAction: {
			label: 'Sample Action',
			icon: 'dashicons-star-filled',
			description: 'This is a sample external action',
			requiresContent: false,
			handler: async function( postId ) {
				// Simulate some processing time
				await new Promise(resolve => setTimeout(resolve, 1000));
				
				return {
					success: true,
					message: 'Sample action completed successfully!'
				};
			}
		},
		writeHeadline: {
			label: 'Write Headline',
			icon: 'dashicons-edit',
			description: 'AI writes a new headline and saves it',
			requiresContent: false,
			handler: async function( postId ) {
				const apply = [ { action: 'update_post_title', from: 'title' } ];
				try {
					await runRecipeAndApply( { recipe: 'write-headline-sidebar', postId, apply } );
					return { success: true, message: 'Headline updated' };
				} catch ( error ) {
					console.error( 'writeHeadline failed:', error );
					return { success: false, message: error?.message || 'Action failed' };
				}
			}
		},
		formatHeadline: {
			label: 'Format My Headline',
			icon: 'dashicons-editor-spellcheck',
			description: 'Formats the existing headline and saves it',
			requiresContent: false,
			handler: async function( postId ) {
				const apply = [ { action: 'update_post_title', from: 'title' } ];
				try {
					await runRecipeAndApply( { recipe: 'format-my-headline', postId, apply } );
					return { success: true, message: 'Headline formatted' };
				} catch ( error ) {
					console.error( 'formatHeadline failed:', error );
					return { success: false, message: error?.message || 'Action failed' };
				}
			}
		},
		formatContent: {
			label: 'Format Content',
			icon: 'dashicons-editor-paragraph',
			description: 'Formats the story content (HTML) and saves it',
			requiresContent: false,
			handler: async function( postId ) {
				const apply = [ { action: 'update_post_content', from: 'content' } ];
				try {
					await runRecipeAndApply( { recipe: 'format-story', postId, apply } );
					return { success: true, message: 'Content formatted' };
				} catch ( error ) {
					console.error( 'formatContent failed:', error );
					return { success: false, message: error?.message || 'Action failed' };
				}
			}
		},
		writeSummary: {
			label: 'Write Summary',
			icon: 'dashicons-media-text',
			description: 'AI writes a concise summary and saves it as the excerpt',
			requiresContent: false,
			handler: async function( postId ) {
				const apply = [ { action: 'update_post_excerpt', from: 'excerpt' } ];
				try {
					await runRecipeAndApply( { recipe: 'summary-generator', postId, apply } );
					return { success: true, message: 'Excerpt updated' };
				} catch ( error ) {
					console.error( 'writeSummary failed:', error );
					return { success: false, message: error?.message || 'Action failed' };
				}
			}
		}
	};
	
	// Revision state
	let currentRevisions = [];
	let currentRevisionIndex = -1; // -1 means current/live version
	let isComparingRevision = false;

	// Wait for DOM to be ready
	document.addEventListener( 'DOMContentLoaded', function() {
		initializeSidebar();
	} );

	function initializeSidebar() {
		const block = document.querySelector( '.xtra-post-editor-sidebar-block' );
		if ( ! block ) return;

		// Get configuration from PHP
		const config = window.xtraPostEditorSidebar || {};
		if ( ! config.enabledActions ) return;

		const triggerButton = block.querySelector( '.xtra-post-editor-sidebar-trigger' );
		const sidebar = block.querySelector( '.xtra-post-editor-sidebar' );
		const closeButton = block.querySelector( '.xtra-post-editor-sidebar-close' );
		const actionsContainer = block.querySelector( '.xtra-sidebar-actions-list' );

		if ( ! triggerButton || ! sidebar || ! actionsContainer ) return;

		let isOpen = false;

		// Populate actions
		populateActions( actionsContainer, config.enabledActions, config.postId );
		
		// Initialize revisions if we have a post ID
		if ( config.postId ) {
			initializeRevisions( config.postId );
		}
		
		// Re-populate actions when content changes (for create mode)
		if ( ! config.postId ) {
			const contentField = document.querySelector( '.xtra-field--content .wp-editor-area, textarea[name="content"]' );
			if ( contentField ) {
				let debounceTimer;
				contentField.addEventListener( 'input', () => {
					clearTimeout( debounceTimer );
					debounceTimer = setTimeout( () => {
						populateActions( actionsContainer, config.enabledActions, config.postId );
					}, 500 );
				} );
			}
		}

		// Toggle sidebar
		const toggleSidebar = () => {
			if ( isOpen ) {
				closeSidebar();
			} else {
				openSidebar();
			}
		};

		// Open sidebar
		const openSidebar = () => {
			isOpen = true;
			sidebar.classList.add( 'is-open' );
			sidebar.setAttribute( 'aria-hidden', 'false' );
			triggerButton.setAttribute( 'aria-expanded', 'true' );
		};

		// Close sidebar
		const closeSidebar = () => {
			isOpen = false;
			sidebar.classList.remove( 'is-open' );
			sidebar.setAttribute( 'aria-hidden', 'true' );
			triggerButton.setAttribute( 'aria-expanded', 'false' );
		};

		// Event listeners
		triggerButton.addEventListener( 'click', toggleSidebar );
		
		if ( closeButton ) {
			closeButton.addEventListener( 'click', closeSidebar );
		}

		// Close on escape key (closes ALL sidebars)
		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' ) {
				// Close this sidebar if open
				if ( isOpen ) {
					closeSidebar();
				}
				// Also close AI chat sidebar if it exists and is open
				const aiChatSidebar = document.querySelector( '.ai-post-chat-sidebar.is-open' );
				if ( aiChatSidebar ) {
					aiChatSidebar.classList.remove( 'is-open' );
					aiChatSidebar.setAttribute( 'aria-hidden', 'true' );
					const aiTrigger = document.querySelector( '.ai-post-chat-trigger' );
					if ( aiTrigger ) {
						aiTrigger.setAttribute( 'aria-expanded', 'false' );
					}
				}
			}
		} );

		// No click-outside closing - sidebars stay open until explicitly closed
	}

	function populateActions( container, enabledActions, postId ) {
		container.innerHTML = '';

		// Create action buttons
		Object.entries( enabledActions ).forEach( ( [ key, enabled ] ) => {
			if ( ! enabled ) return;
			
			const action = actionRegistry[ key ];
			if ( ! action ) return;

			const button = createActionButton( action, key, postId );
			container.appendChild( button );
		} );

		// If no tools are enabled
		if ( container.children.length === 0 ) {
			container.innerHTML = '<p class="xtra-sidebar-no-actions">No tools enabled</p>';
		}
	}

	function createActionButton( action, key, postId ) {
		const button = document.createElement( 'button' );
		button.className = 'xtra-sidebar-action-button';
		button.dataset.action = key;
		button.type = 'button';

		// Check if action should be disabled
		let isDisabled = false;
		let disabledReason = '';
		
		if ( action.requiresContent && ! postId ) {
			// In create mode, check if there's any content
			const contentField = document.querySelector( '.xtra-field--content .wp-editor-area, textarea[name="content"]' );
			const hasContent = contentField && contentField.value.trim().length > 0;
			
			if ( ! hasContent ) {
				isDisabled = true;
				disabledReason = ' (requires content)';
			}
		}

		button.innerHTML = `
			<span class="xtra-action-icon dashicons ${action.icon}"></span>
			<div class="xtra-action-content">
				<span class="xtra-action-label">${action.label}</span>
				<span class="xtra-action-description">${action.description}${disabledReason}</span>
			</div>
			<span class="xtra-action-spinner"></span>
		`;

		if ( isDisabled ) {
			button.disabled = true;
			button.classList.add( 'is-disabled' );
		} else {
			button.addEventListener( 'click', async () => {
				await handleActionClick( button, action, postId );
			} );
		}

		return button;
	}

	async function handleActionClick( button, action, postId ) {
		// Prevent double clicks
		if ( button.classList.contains( 'is-loading' ) ) return;

		button.classList.add( 'is-loading' );
		button.disabled = true;

		try {
			// Run the action handler
			const result = await action.handler( postId );

			if ( result.success ) {
				// Show persistent success toast with Refresh button (no auto-reload)
				const message = result.message || 'Action completed successfully';
				if ( window.XtraXtraToast && typeof window.XtraXtraToast.show === 'function' ) {
					const url = window.location.href.split('#')[0];
					window.XtraXtraToast.show( message, 'success', {
						persistent: true,
						buttons: [ { text: 'Refresh', url } ]
					} );
				} else {
					showToast( 'success', message );
				}
			} else {
				showToast( 'error', result.message || 'Action failed' );
			}
		} catch ( error ) {
			console.error( 'Action failed:', error );
			showToast( 'error', 'Action failed' );
		} finally {
			button.classList.remove( 'is-loading' );
			button.disabled = false;
		}
	}

	function showToast( type, message ) {
		// Use XtraXtra toast if available
		if ( window.XtraXtraToast ) {
			if ( type === 'success' ) {
				window.XtraXtraToast.success( message );
			} else {
				window.XtraXtraToast.error( message );
			}
		}
	}
	
	// ===== REVISION FUNCTIONS =====
	
	async function initializeRevisions( postId ) {
		// Add revision controls container to sidebar
		const sidebar = document.querySelector( '.xtra-post-editor-sidebar-panel' );
		if ( ! sidebar ) return;
		
		// Find the content area or create a new section after it
		const contentArea = sidebar.querySelector( '.xtra-post-editor-sidebar-content' );
		if ( ! contentArea ) return;
		
		// Create revisions section (starts collapsed)
		const revisionsSection = document.createElement( 'div' );
		revisionsSection.className = 'xtra-sidebar-revisions-section';
		revisionsSection.innerHTML = `
			<h3 class="xtra-sidebar-section-title xtra-revisions-toggle">
				<span class="dashicons dashicons-arrow-right-alt2 xtra-toggle-icon"></span>
				Revisions
			</h3>
			<div class="xtra-revisions-controls">
				<div class="xtra-revision-info">
					<span class="revision-status">Current Version</span>
				</div>
				<div class="xtra-revision-navigation">
					<button type="button" class="xtra-revision-nav-btn xtra-revision-prev" disabled>
						<span class="dashicons dashicons-arrow-left-alt2"></span>
						Previous
					</button>
					<button type="button" class="xtra-revision-nav-btn xtra-revision-next" disabled>
						Next
						<span class="dashicons dashicons-arrow-right-alt2"></span>
					</button>
				</div>
				<div class="xtra-revision-actions">
					<button type="button" class="xtra-revision-compare-btn" disabled>
						<span class="dashicons dashicons-editor-code"></span>
						Compare to Saved
					</button>
					<button type="button" class="xtra-revision-restore-btn" disabled>
						<span class="dashicons dashicons-backup"></span>
						Restore This Version
					</button>
				</div>
			</div>
		`;
		
		// Insert after content area (before footer)
		const footer = sidebar.querySelector( '.xtra-post-editor-sidebar-footer' );
		if ( footer ) {
			sidebar.insertBefore( revisionsSection, footer );
		} else {
			sidebar.appendChild( revisionsSection );
		}
		
		// Setup event listeners
		setupRevisionListeners( postId );
		
		// Add toggle functionality
		const toggleBtn = revisionsSection.querySelector( '.xtra-revisions-toggle' );
		const controlsDiv = revisionsSection.querySelector( '.xtra-revisions-controls' );
		const toggleIcon = revisionsSection.querySelector( '.xtra-toggle-icon' );
		let isExpanded = false;
		
		// Make sure it starts collapsed (remove is-expanded class if present)
		revisionsSection.classList.remove( 'is-expanded' );
		
		toggleBtn.addEventListener( 'click', async () => {
			isExpanded = !isExpanded;
			
			if ( isExpanded ) {
				// Expand and load revisions
				toggleIcon.classList.remove( 'dashicons-arrow-right-alt2' );
				toggleIcon.classList.add( 'dashicons-arrow-down-alt2' );
				revisionsSection.classList.add( 'is-expanded' );
				
				// Load fresh revisions
				await loadRevisions( postId );
			} else {
				// Collapse
				toggleIcon.classList.remove( 'dashicons-arrow-down-alt2' );
				toggleIcon.classList.add( 'dashicons-arrow-right-alt2' );
				revisionsSection.classList.remove( 'is-expanded' );
			}
		} );
		
		// Watch for post saves to auto-collapse
		watchForPostSaves( postId, revisionsSection, () => {
			isExpanded = false;
		});
		
		// Load just the count initially (lightweight request)
		loadRevisionCount( postId );
	}
	
	async function loadRevisionCount( postId ) {
		const config = window.xtraPostEditorSidebar || {};
		
		try {
			const response = await fetch( `${config.apiUrl}xtra-blocks/v1/revisions/${postId}`, {
				method: 'GET',
				headers: {
					'X-WP-Nonce': config.nonce,
					'Content-Type': 'application/json'
				},
				credentials: 'same-origin'
			} );
			
			if ( ! response.ok ) {
				return;
			}
			
			const data = await response.json();
			const revisions = data.revisions || [];
			
		} catch ( error ) {
			// Silently fail for count loading
		}
	}
	
	async function loadRevisions( postId ) {
		const config = window.xtraPostEditorSidebar || {};
		
		try {
			const response = await fetch( `${config.apiUrl}xtra-blocks/v1/revisions/${postId}`, {
				method: 'GET',
				headers: {
					'X-WP-Nonce': config.nonce,
					'Content-Type': 'application/json'
				},
				credentials: 'same-origin'
			} );
			
			if ( ! response.ok ) {
				throw new Error( 'Failed to load revisions' );
			}
			
			const data = await response.json();
			currentRevisions = data.revisions || [];
			currentRevisionIndex = -1; // Always start at current version
			
			
			updateRevisionUI();
			
		} catch ( error ) {
			console.error( 'Failed to load revisions:', error );
		}
	}
	
	function setupRevisionListeners( postId ) {
		const prevBtn = document.querySelector( '.xtra-revision-prev' );
		const nextBtn = document.querySelector( '.xtra-revision-next' );
		const compareBtn = document.querySelector( '.xtra-revision-compare-btn' );
		const restoreBtn = document.querySelector( '.xtra-revision-restore-btn' );
		
		if ( prevBtn ) {
			prevBtn.addEventListener( 'click', () => navigateRevision( 'prev', postId ) );
		}
		
		if ( nextBtn ) {
			nextBtn.addEventListener( 'click', () => navigateRevision( 'next', postId ) );
		}
		
		if ( compareBtn ) {
			compareBtn.addEventListener( 'click', () => compareRevision( postId ) );
		}
		
		if ( restoreBtn ) {
			restoreBtn.addEventListener( 'click', () => restoreRevision( postId ) );
		}
	}
	
	function navigateRevision( direction, postId ) {
		if ( direction === 'prev' ) {
			// Going back in time (older revisions)
			if ( currentRevisionIndex === -1 && currentRevisions.length > 0 ) {
				// From current view to the latest stored revision (index 0)
				currentRevisionIndex = 0;
			} else if ( currentRevisionIndex < currentRevisions.length - 1 ) {
				currentRevisionIndex++;
			}
		} else {
			// Going forward in time (newer revisions)
			if ( currentRevisionIndex > 0 ) {
				currentRevisionIndex--;
			} else if ( currentRevisionIndex === 0 ) {
				// Back to current (live) version
				currentRevisionIndex = -1;
			}
		}
		
		updateRevisionUI();
		// Don't load the revision content anymore - just update the UI
	}
	
	function updateRevisionUI() {
		const prevBtn = document.querySelector( '.xtra-revision-prev' );
		const nextBtn = document.querySelector( '.xtra-revision-next' );
		const compareBtn = document.querySelector( '.xtra-revision-compare-btn' );
		const restoreBtn = document.querySelector( '.xtra-revision-restore-btn' );
		const statusSpan = document.querySelector( '.revision-status' );
		
    if ( currentRevisionIndex === -1 ) {
        // Current (live) version – no confusing counters
        if ( currentRevisions.length > 0 ) {
            const latestRev = currentRevisions[0];
            const date = new Date( latestRev.created_at );
            const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString( [], { hour: '2-digit', minute: '2-digit' } );
            statusSpan.innerHTML = `Current Version<br><small>Latest revision: ${dateStr}</small>`;
        } else {
            statusSpan.textContent = 'Current Version';
        }
			// Enable Previous if we have at least 1 stored revision
			prevBtn.disabled = currentRevisions.length < 1;
			nextBtn.disabled = true;
			// Disable Compare and Restore buttons when on current version
			if ( compareBtn ) compareBtn.disabled = true;
			if ( restoreBtn ) restoreBtn.disabled = true;
    } else {
        // Viewing a specific stored revision
        const revision = currentRevisions[ currentRevisionIndex ];
        const total = currentRevisions.length;
        // Human-friendly numbering: 1 = newest stored, total = oldest
        const revIndex = currentRevisionIndex + 1;
        const date = new Date( revision.created_at );
        const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString( [], { hour: '2-digit', minute: '2-digit' } );
        statusSpan.innerHTML = `Revision ${revIndex} of ${total}${revIndex === 1 ? ' (Newest)' : revIndex === total ? ' (Oldest)' : ''}<br><small>${dateStr}</small>`;
			
			prevBtn.disabled = currentRevisionIndex >= currentRevisions.length - 1;
			nextBtn.disabled = false;
			// Enable Compare and Restore buttons when viewing a revision
			if ( compareBtn ) compareBtn.disabled = false;
			if ( restoreBtn ) restoreBtn.disabled = false;
		}
	}
	async function compareRevision( postId ) {
		if ( currentRevisionIndex === -1 ) return;
		
		const revision = currentRevisions[ currentRevisionIndex ];
		const config = window.xtraPostEditorSidebar || {};
		
		try {
			const response = await fetch( `${config.apiUrl}xtra-blocks/v1/revisions/compare/${revision.id}`, {
				method: 'GET',
				headers: {
					'X-WP-Nonce': config.nonce,
					'Content-Type': 'application/json'
				},
				credentials: 'same-origin'
			} );
			
			if ( ! response.ok ) {
				throw new Error( 'Failed to load comparison' );
			}
			
			const data = await response.json();
			showComparisonModal( data );
			
		} catch ( error ) {
			console.error( 'Failed to compare:', error );
			showToast( 'error', 'Failed to load comparison' );
		}
	}
	
	function showComparisonModal( data ) {
		// Create modal
		const modal = document.createElement( 'div' );
		modal.className = 'xtra-revision-modal';
		modal.innerHTML = `
			<div class="xtra-revision-modal-content">
				<div class="xtra-revision-modal-header">
					<h2>Compare Revision to Saved</h2>
					<button type="button" class="xtra-modal-close">
						<span class="dashicons dashicons-no"></span>
					</button>
				</div>
				<div class="xtra-revision-modal-body">
					<div class="xtra-comparison-grid">
						${generateComparisonHTML( data )}
					</div>
				</div>
			</div>
		`;
		
		document.body.appendChild( modal );
		
		// Close button
		modal.querySelector( '.xtra-modal-close' ).addEventListener( 'click', () => {
			modal.remove();
		} );
		
		// Close on escape
		const escHandler = ( e ) => {
			if ( e.key === 'Escape' ) {
				modal.remove();
				document.removeEventListener( 'keydown', escHandler );
			}
		};
		document.addEventListener( 'keydown', escHandler );
		
		// Close on background click
		modal.addEventListener( 'click', ( e ) => {
			if ( e.target === modal ) {
				modal.remove();
			}
		} );
	}
	
	function generateComparisonHTML( data ) {
		const fields = [
			{ key: 'post_title', label: 'Title', type: 'text' },
			{ key: 'post_content', label: 'Content', type: 'html' },
			{ key: 'post_excerpt', label: 'Excerpt', type: 'text' }
		];
		
		let html = '';
		
	fields.forEach( field => {
		const revisionValue = data.revision[ field.key ] || '';
		const currentValue = data.current[ field.key ] || '';
		
		// Normalize values to avoid false positives from whitespace/block comments
		const revNorm = field.type === 'html' ? normalizeHtmlForDiff( revisionValue ) : normalizeTextForDiff( revisionValue );
		const curNorm = field.type === 'html' ? normalizeHtmlForDiff( currentValue ) : normalizeTextForDiff( currentValue );
		
		if ( revNorm !== curNorm ) {
				let diffHtml;
				
				if ( field.type === 'html' ) {
				// For HTML content, normalize + show rendered HTML with diff highlights
				const cleanRevision = normalizeHtmlForDiff( revisionValue );
				const cleanCurrent = normalizeHtmlForDiff( currentValue );
					
					const diff = createSplitDiff( cleanRevision, cleanCurrent );
					
					// Create a side-by-side diff view
					diffHtml = `
						<div class="html-comparison">
							<div class="html-col deletions-col">
								<h4><span class="dashicons dashicons-minus"></span> Removed</h4>
								<div class="html-content">${diff.deletions}</div>
							</div>
							<div class="html-col additions-col">
								<h4><span class="dashicons dashicons-plus"></span> Added</h4>
								<div class="html-content">${diff.additions}</div>
							</div>
						</div>
					`;
				} else {
					// For plain text, use split diff as well
					const diff = createSplitDiff( revisionValue, currentValue );
					diffHtml = `
						<div class="text-comparison">
							<div class="text-col deletions-col">
								<h4><span class="dashicons dashicons-minus"></span> Removed</h4>
								<div class="text-content">${diff.deletions}</div>
							</div>
							<div class="text-col additions-col">
								<h4><span class="dashicons dashicons-plus"></span> Added</h4>
								<div class="text-content">${diff.additions}</div>
							</div>
						</div>
					`;
				}
				
				html += `
					<div class="comparison-field">
						<h3>${field.label}</h3>
						<div class="comparison-diff">
							${diffHtml}
						</div>
					</div>
				`;
			}
		} );
		
return html || '<p>No differences found.</p>';
}

// Helper: remove Gutenberg block comments from HTML
function stripBlockComments( html ) {
    if ( ! html ) return '';
    let cleaned = String( html ).replace(/<!--\s*\/?wp:[\s\S]*?-->/g, '');
    // Normalize line endings and collapse excessive blank lines
    cleaned = cleaned.replace(/\r\n/g, '\n').replace(/\n\s*\n\s*\n/g, '\n\n');
    return cleaned;
}

// Normalize HTML for diff by removing block comments and collapsing trivial whitespace
function normalizeHtmlForDiff( html ) {
    let s = stripBlockComments( String( html || '' ) );
    s = s.replace(/>\s+</g, '><');
    s = s.replace(/\s{2,}/g, ' ');
    s = s.trim();
    return s;
}

function normalizeTextForDiff( text ) {
    let s = String( text || '' );
    s = s.replace(/\r\n/g, '\n');
    s = s.replace(/\s{2,}/g, ' ');
    s = s.trim();
    return s;
}
	
	function escapeHtml( text ) {
		const div = document.createElement( 'div' );
		div.textContent = text;
		return div.innerHTML;
	}
	
	// Create separate deletion and addition views with word-level precision
	function createSplitDiff( oldText, newText ) {
		// Use word-level diff for precise highlighting
		const { oldWithDiff, newWithDiff } = computeWordDiff( oldText, newText );
		
		return {
			deletions: oldWithDiff,
			additions: newWithDiff
		};
	}
	
	// Compute word-level differences
	function computeWordDiff( oldText, newText ) {
		if (!oldText && !newText) {
			return {
				oldWithDiff: '<span class="no-content">No content</span>',
				newWithDiff: '<span class="no-content">No content</span>'
			};
		}
		
		if (!oldText) {
			return {
				oldWithDiff: '<span class="no-content">No previous content</span>',
				newWithDiff: `<span class="diff-add">${escapeHtml(newText)}</span>`
			};
		}
		
		if (!newText) {
			return {
				oldWithDiff: `<span class="diff-del">${escapeHtml(oldText)}</span>`,
				newWithDiff: '<span class="no-content">Content removed</span>'
			};
		}
		
		// Split texts into words while preserving HTML tags and whitespace
		const oldWords = tokenizeText(oldText);
		const newWords = tokenizeText(newText);
		
		// Find the Longest Common Subsequence
		const lcs = findLCS(oldWords, newWords);
		
		// Build the diff views
		const oldResult = buildDiffView(oldWords, lcs, 'old');
		const newResult = buildDiffView(newWords, lcs, 'new');
		
		return {
			oldWithDiff: oldResult,
			newWithDiff: newResult
		};
	}
	
	// Tokenize text into words, preserving HTML tags and structure
	function tokenizeText(text) {
		// Split by word boundaries but keep HTML tags intact
		const tokens = text.match(/(<[^>]+>|[^\s<>]+|\s+)/g) || [];
		return tokens;
	}
	
	// Find Longest Common Subsequence for accurate diff
	function findLCS(arr1, arr2) {
		const m = arr1.length;
		const n = arr2.length;
		const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
		
		for (let i = 1; i <= m; i++) {
			for (let j = 1; j <= n; j++) {
				if (arr1[i - 1] === arr2[j - 1]) {
					dp[i][j] = dp[i - 1][j - 1] + 1;
				} else {
					dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
				}
			}
		}
		
		// Backtrack to find the actual LCS
		const lcs = new Set();
		let i = m, j = n;
		while (i > 0 && j > 0) {
			if (arr1[i - 1] === arr2[j - 1]) {
				lcs.add(`${i - 1},${j - 1}`);
				i--;
				j--;
			} else if (dp[i - 1][j] > dp[i][j - 1]) {
				i--;
			} else {
				j--;
			}
		}
		
		return lcs;
	}
	
	// Build the diff view with appropriate highlighting
	function buildDiffView(words, lcs, type) {
		let result = '';
		let inDiff = false;
		let diffBuffer = '';
		
		for (let i = 0; i < words.length; i++) {
			const word = words[i];
			const isCommon = Array.from(lcs).some(pos => {
				const [oldIdx, newIdx] = pos.split(',').map(Number);
				return type === 'old' ? oldIdx === i : newIdx === i;
			});
			
			if (isCommon) {
				// This word is common to both versions
				if (inDiff) {
					// Close any open diff span
					result += `<span class="diff-${type === 'old' ? 'del' : 'add'}">${diffBuffer}</span>`;
					diffBuffer = '';
					inDiff = false;
				}
				// Add the common word (keep HTML tags as-is)
				if (word.startsWith('<') && word.endsWith('>')) {
					result += word;
				} else {
					result += escapeHtml(word);
				}
			} else {
				// This word is different
				if (!inDiff) {
					inDiff = true;
				}
				// Add to diff buffer
				if (word.startsWith('<') && word.endsWith('>')) {
					// It's an HTML tag, add it directly
					if (inDiff && diffBuffer) {
						result += `<span class="diff-${type === 'old' ? 'del' : 'add'}">${diffBuffer}</span>`;
						diffBuffer = '';
					}
					result += word;
				} else {
					diffBuffer += escapeHtml(word);
				}
			}
		}
		
		// Close any remaining diff span
		if (inDiff && diffBuffer) {
			result += `<span class="diff-${type === 'old' ? 'del' : 'add'}">${diffBuffer}</span>`;
		}
		
		return result || '<span class="no-content">No content</span>';
	}
	
	async function restoreRevision( postId ) {
		if ( currentRevisionIndex === -1 ) return;
		
		const revision = currentRevisions[ currentRevisionIndex ];
		const revNum = currentRevisions.length - currentRevisionIndex;
		
		if ( ! confirm( `Are you sure you want to restore Revision ${revNum}? This will replace your current content.` ) ) {
			return;
		}
		
		const config = window.xtraPostEditorSidebar || {};
		
		// Show loading state
		const restoreBtn = document.querySelector( '.xtra-revision-restore-btn' );
		if ( restoreBtn ) {
			restoreBtn.disabled = true;
			restoreBtn.innerHTML = '<span class="dashicons dashicons-update spin"></span> Restoring...';
		}
		
		try {
			const response = await fetch( `${config.apiUrl}xtra-blocks/v1/revision/${revision.id}/restore`, {
				method: 'POST',
				headers: {
					'X-WP-Nonce': config.nonce,
					'Content-Type': 'application/json'
				},
				credentials: 'same-origin'
			} );
			
			if ( ! response.ok ) {
				throw new Error( 'Failed to restore revision' );
			}
			
			showToast( 'success', 'Revision restored! Reloading...' );
			
			// Reload the page to show restored content
			setTimeout( () => {
				location.reload();
			}, 500 );
			
		} catch ( error ) {
			console.error( 'Failed to restore:', error );
			showToast( 'error', 'Failed to restore revision' );
			
			// Reset button
			if ( restoreBtn ) {
				restoreBtn.disabled = false;
				restoreBtn.innerHTML = '<span class="dashicons dashicons-backup"></span> Restore This Version';
			}
		}
	}
	
	// Watch for post saves to auto-collapse revisions
	function watchForPostSaves( postId, revisionsSection, onCollapse ) {
		// Helper function to collapse the revisions
		const collapseRevisions = () => {
			const toggleIcon = revisionsSection.querySelector( '.xtra-toggle-icon' );
			
			if ( toggleIcon ) {
				toggleIcon.classList.remove( 'dashicons-arrow-down-alt2' );
				toggleIcon.classList.add( 'dashicons-arrow-right-alt2' );
				revisionsSection.classList.remove( 'is-expanded' );
				
				// Call the callback to update the isExpanded state
				if ( onCollapse ) {
					onCollapse();
				}
			}
		};
		
		// Method 1: Listen for the Update button click - catch all possible save buttons
		// Use a slight delay to ensure dynamically added buttons are present
		setTimeout( () => {
			// Check for buttons by their text content
			const allButtons = document.querySelectorAll( 'button' );
			allButtons.forEach( button => {
				const text = button.textContent || '';
				if ( text.includes( 'Update Post' ) || text.includes( 'Create Post' ) || text.includes( 'Save' ) || text.includes( 'Publish' ) ) {
					button.addEventListener( 'click', () => {
						setTimeout( collapseRevisions, 500 );
					} );
				}
			} );
			
			// Also add to specifically matched selector buttons
			const updateButtons = document.querySelectorAll( 
				'.components-button.is-primary, button[type="submit"], .wp-element-button'
			);
			updateButtons.forEach( button => {
				button.addEventListener( 'click', () => {
					setTimeout( collapseRevisions, 500 );
				} );
			} );
		}, 1000 );
		
		// Method 2: Listen for WordPress/Gutenberg save events if available
		if ( window.wp && window.wp.data ) {
			const { subscribe } = wp.data;
			let wasSaving = false;
			
			const unsubscribe = subscribe( () => {
				const editor = wp.data.select( 'core/editor' );
				if ( ! editor ) return;
				
				const isSaving = editor.isSavingPost();
				const isAutosaving = editor.isAutosavingPost();
				
				// When save completes (transitions from saving to not saving)
				if ( wasSaving && ! isSaving && ! isAutosaving ) {
					// Post just finished saving, collapse revisions
					collapseRevisions();
				}
				
				wasSaving = isSaving;
			} );
		}
		
		// Method 3: Watch for AJAX success on classic editor
		if ( typeof jQuery !== 'undefined' ) {
			jQuery( document ).ajaxSuccess( function( event, xhr, settings ) {
				// Check if this was a post save
				if ( settings.data && settings.data.includes( 'action=editpost' ) ) {
					collapseRevisions();
				}
			} );
		}
		
		// Method 4: Watch for XtraXtra specific save events
		document.addEventListener( 'xtra:post-saved', () => {
			collapseRevisions();
		} );
		
		// Method 5: Watch for form submissions on the page
		const postForm = document.querySelector( 'form#post, form.xtra-post-editor-form' );
		if ( postForm ) {
			postForm.addEventListener( 'submit', () => {
				setTimeout( collapseRevisions, 1000 );
			} );
		}
		
		// Method 6: Watch for changes to success messages that indicate a save
		const observer = new MutationObserver( ( mutations ) => {
			mutations.forEach( ( mutation ) => {
				if ( mutation.type === 'childList' ) {
					mutation.addedNodes.forEach( ( node ) => {
						if ( node.nodeType === 1 ) { // Element node
							const text = node.textContent || '';
							// Check for success messages
							if ( text.includes( 'Post updated' ) || text.includes( 'Post created' ) || text.includes( 'successfully' ) ) {
								setTimeout( collapseRevisions, 100 );
							}
						}
					} );
				}
			} );
		} );
		
		// Start observing the document body for changes
		observer.observe( document.body, {
			childList: true,
			subtree: true
		} );
	}

} )();
